import React, { useEffect, useRef, useState } from "react";

import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import MainContainer from "components/MainContainer";
import { isDev } from "config/env";
import {
  setActualHeldDetail,
  setOriginalHeldDetail,
} from "features/slices/reservationFlowSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { ScheduleStackParamList } from "navigation/types";
import {
  holdForTflEventRequest,
  useFetchConsumerKeyQuery,
  useFetchTflTicketListingsForEventQuery,
} from "queries/ticket.queries";
import { View } from "react-native";
import { cacheItem, getCachedItem } from "utils/cacheUtil";
import "./style.css";
interface TflTicketPayload {
  type: "ADD_TO_CART" | "SEATMAP_READY";
  payload: {
    ticket: {
      tgID: string; // Tfl ticket listing id
      tgUserRow: string;
      tgUserSec: string;
    };
    qty: number | null;
  };
}
const TflSeatSelection = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [seatMapReady, setSeatMapReady] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const { selectedEvent, selectedSubscription } = useAppSelector(
    (state) => state.reservationFlow
  );

  const dispatch = useAppDispatch();
  const navigation =
    useNavigation<
      NativeStackNavigationProp<ScheduleStackParamList, "TflSeatSelection">
    >();
  const { data: consumerKey } = useFetchConsumerKeyQuery();
  const { mutateAsync: holdForTflEvent } = useMutation(holdForTflEventRequest);
  const { data: tflTicketListings } = useFetchTflTicketListingsForEventQuery(
    selectedEvent?.extEventId!,
    selectedSubscription?.planPackage.id!
  );

  const injectSeatMapScript = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument || !consumerKey) return;

    const scriptSrc = `https://${
      isDev ? "sandbox" : "prod"
    }.tflmaps.com/Maps/mapandlayout?eventId=${
      selectedEvent?.extEventId
    }&consumerKey=${consumerKey.value}`;
    const scriptId = "tfl-map-script";

    if (iframe.contentDocument.getElementById(scriptId)) {
      return;
    }

    const script = iframe.contentDocument.createElement("script");
    script.id = scriptId;
    script.src = scriptSrc;

    const mapHolder = iframe.contentDocument.querySelector(".map-holder");
    mapHolder?.appendChild(script);
  };

  const injectTicketListingScript = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument || !consumerKey) return;

    const scriptId = "tfl-ticket-script";

    if (iframe.contentDocument.getElementById(scriptId)) {
      return;
    }
    // Send the ticket listings via postMessage
    iframe.contentWindow?.postMessage(
      {
        type: "ADD_TICKET_LISTINGS",
        listings: tflTicketListings,
      },
      "*"
    );
  };
  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (typeof event.data !== "string") return; // Event data posted by iframe is string

      const data = JSON.parse(event.data) as TflTicketPayload;
      if (data.type === "ADD_TO_CART") {
        const { tgID } = data.payload.ticket;
        const { qty } = data.payload;
        const { eventId } = selectedEvent!;
        const { planPackage } = selectedSubscription!;
        const heldTickets = await holdForTflEvent({
          eventId,
          planPackageId: planPackage.id,
          quantity: qty || 1,
          ticketListingId: tgID,
          section: data.payload.ticket.tgUserSec,
          row: data.payload.ticket.tgUserRow,
        });
        dispatch(
          setOriginalHeldDetail({
            section: data.payload.ticket.tgUserSec,
            row: data.payload.ticket.tgUserRow,
          })
        );
        dispatch(setActualHeldDetail(heldTickets));
        navigation.navigate("Reserve");
      }
      if (data.type === "SEATMAP_READY") {
        console.log("Seatmap ready");
        setSeatMapReady(true);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Function to inject the script into the iframe on load
  const injectScriptOnLoad = () => {
    injectSeatMapScript();
    injectTicketListingScript();
  };

  useEffect(() => {
    const fetchHtml = async () => {
      // Try to get cached HTML first
      const cachedHtml = getCachedItem("tfl_seatmap_html");
      if (cachedHtml) {
        setHtmlContent(cachedHtml);
        return;
      }

      // If not cached, fetch and cache
      try {
        const url = isDev ? "/tfl_sandbox_seatmap.html" : "/tfl_seatmap.html";
        const response = await fetch(url);
        const html = await response.text();
        cacheItem("tfl_seatmap_html", html);
        setHtmlContent(html);
      } catch (error) {
        console.error("Failed to fetch seat map HTML:", error);
      }
    };

    fetchHtml();
  }, []);

  return (
    <MainContainer>
      {!seatMapReady && (
        <View className="flex absolute top-0 left-0 right-0 bottom-0 items-center justify-center flex-1">
          <div className="loader"></div>
          <Text>Loading Your Tickets...</Text>
        </View>
      )}
      {tflTicketListings && (
        <>
          {consumerKey && htmlContent && (
            <iframe
              ref={iframeRef}
              srcDoc={htmlContent}
              style={{
                width: "100%",
                height: "calc(100dvh - 50px)",
                border: "none",
              }}
              title="TFL Seat Selection Map"
              onLoad={injectScriptOnLoad}
            />
          )}
        </>
      )}
    </MainContainer>
  );
};

export default TflSeatSelection;
